// src/hooks/useKeyboardShortcuts.ts
// Reusable keyboard shortcut handler with presets for common actions

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { nothingOpenAtKeydown, pushShortcuts, shortcutsMayAct } from "@em/ui";

// ============================================
// Types
// ============================================

export interface KeyboardShortcut {
  /** Key to listen for (e.g., 'n', 'r', 'Escape') */
  key: string;

  /** Require Ctrl (Windows/Linux) or Cmd (Mac) */
  ctrl?: boolean;

  /** Require Shift key */
  shift?: boolean;

  /** Require Alt (Windows/Linux) or Option (Mac) */
  alt?: boolean;

  /** Handler function to execute */
  action: () => void;

  /** Description for help/accessibility */
  description?: string;

  /** Whether shortcut is currently enabled (default: true) */
  enabled?: boolean;

  /** Prevent default browser behavior (default: true) */
  preventDefault?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  /** Array of shortcuts to register */
  shortcuts: KeyboardShortcut[];

  /** Global enable/disable for all shortcuts (default: true) */
  enabled?: boolean;

  /** Scope identifier for debugging */
  scope?: string;

  /** Whether to allow shortcuts while in input fields (default: false) */
  allowInInputs?: boolean;

  /**
   * A PAGE's shortcuts: act only when no drawer / modal / palette was open when the key went down (@em/ui overlayStack) —
   * so ⌘S, Esc or Delete never reach the page behind an open drawer, whatever the page's own `enabled` says.
   */
  onlyWhenNothingOpen?: boolean;
}

// ============================================
// Main Hook
// ============================================

export const useKeyboardShortcuts = (
  options: UseKeyboardShortcutsOptions
): void => {
  const { shortcuts, enabled = true, scope, allowInInputs = false, onlyWhenNothingOpen = false } = options;

  useEffect(() => {
    if (!enabled || shortcuts.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (onlyWhenNothingOpen && !nothingOpenAtKeydown()) {
        return;
      }

      // Detect platform for Ctrl/Cmd handling
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const ctrlKey = isMac ? event.metaKey : event.ctrlKey;

      // Skip if user is typing in an input (unless explicitly allowed). We still
      // allow Escape, any Ctrl/Cmd-modified shortcut (e.g. ⌘S to save), and the
      // function keys (F1–F12 never produce text) so those work while a form
      // field is focused — only bare printable keys are blocked.
      if (!allowInInputs) {
        const target = event.target as HTMLElement;
        const tagName = target?.tagName?.toUpperCase();

        const inInput =
          tagName === "INPUT" ||
          tagName === "TEXTAREA" ||
          tagName === "SELECT" ||
          target?.isContentEditable;

        const isFunctionKey = /^F\d{1,2}$/.test(event.key);

        if (inInput && event.key !== "Escape" && !ctrlKey && !isFunctionKey) {
          return;
        }
      }

      // An Esc that belongs to something above (an open popover — see @em/ui overlayStack — or a Mantine Menu item) is
      // marked the way Mantine marks it; a drawer's / page's Esc does not act on it.
      if (event.key === "Escape" && (event.target as HTMLElement | null)?.getAttribute?.("data-mantine-stop-propagation") === "true") {
        return;
      }

      for (const shortcut of shortcuts) {
        // Skip disabled shortcuts
        if (shortcut.enabled === false) {
          continue;
        }

        // Check key match (case-insensitive)
        const keyMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase() ||
          event.code.toLowerCase() === `key${shortcut.key.toLowerCase()}`;

        // Check modifier keys
        const ctrlMatch = shortcut.ctrl ? ctrlKey : !ctrlKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        // console.log("=== shortcut", shortcut, {
        //   eventKey: event.key,
        //   eventCode: event.code,
        //   keyMatch,
        //   ctrlMatch,
        //   shiftMatch,
        //   altMatch,
        // });

        if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
          // Prevent default unless explicitly set to false
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
            event.stopPropagation();
          }

          // Log for debugging if scope is provided
          if (scope && process.env.NODE_ENV === "development") {
            console.debug(
              `[Keyboard:${scope}] Triggered: ${shortcut.description || shortcut.key
              }`
            );
          }

          shortcut.action();
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [shortcuts, enabled, scope, allowInInputs, onlyWhenNothingOpen]);
};

// ============================================
// Entity Page Shortcuts Preset
// ============================================

export interface EntityPageShortcutsOptions {
  /** Handler for adding new record (Ctrl+N) */
  onAdd?: () => void;

  /** Handler for refreshing data (Ctrl+R) */
  onRefresh?: () => void;

  /** Handler for closing/canceling (Escape) */
  onEscape?: () => void;

  /** Handler for saving (Ctrl+S) */
  onSave?: () => void;

  /** Handler for deleting (Ctrl+D or Delete) */
  onDelete?: () => void;

  /** Handler for focusing the page/header search input (Ctrl/Cmd+F) */
  onFocusSearch?: () => void;

  /** Handler for opening the header overflow (⋯) menu (F9) */
  onOpenMenu?: () => void;

  /** Handler for clearing/resetting the page's applied filters (F8) */
  onResetFilters?: () => void;

  /** Whether add action is allowed */
  canAdd?: boolean;

  /** Whether refresh action is allowed */
  canRefresh?: boolean;

  /** Whether save action is allowed */
  canSave?: boolean;

  /** Whether delete action is allowed */
  canDelete?: boolean;

  /** Whether the focus-search action is allowed (default: true) */
  canFocusSearch?: boolean;

  /** Whether the open-menu action is allowed (default: true) */
  canOpenMenu?: boolean;

  /** Whether the reset-filters action is allowed (default: true) */
  canResetFilters?: boolean;

  /**
   * Additional, page-specific shortcuts appended to the standard set — the
   * extension point for things like tab switching. Registered as-is.
   */
  extraShortcuts?: KeyboardShortcut[];

  /** Whether shortcuts are enabled */
  enabled?: boolean;
}

/**
 * Preset hook for common entity page shortcuts.
 *
 * Standard bindings: Ctrl+I (add), Ctrl+R (refresh), Ctrl+S (save), Escape
 * (close), Delete (delete), Ctrl/Cmd+F (focus search), F9 (open overflow menu),
 * F8 (reset filters). Pass `extraShortcuts` for page-specific keys (e.g. tab
 * switching). Every binding is opt-in — it only registers when its handler is
 * provided.
 */
export const useEntityPageShortcuts = (
  options: EntityPageShortcutsOptions
): void => {
  const {
    onAdd,
    onRefresh,
    onEscape,
    onSave,
    onDelete,
    onFocusSearch,
    onOpenMenu,
    onResetFilters,
    canAdd = true,
    canRefresh = true,
    canSave = true,
    canDelete = true,
    canFocusSearch = true,
    canOpenMenu = true,
    canResetFilters = true,
    extraShortcuts,
    enabled = true,
  } = options;

  const shortcuts = useMemo<KeyboardShortcut[]>(() => {
    const result: KeyboardShortcut[] = [];

    // Ctrl+N: Add new record
    if (onAdd) {
      result.push({
        key: "i",
        ctrl: true,
        action: onAdd,
        description: "Add new record",
        enabled: canAdd,
        preventDefault: true,
      });
    }

    // Ctrl+R: Refresh data
    if (onRefresh) {
      result.push({
        key: "r",
        ctrl: true,
        action: onRefresh,
        description: "Refresh data",
        enabled: canRefresh,
        preventDefault: true, // Prevent browser refresh
      });
    }

    // Escape: Close/Cancel
    if (onEscape) {
      result.push({
        key: "Escape",
        action: onEscape,
        description: "Close/Cancel",
        enabled: true,
        preventDefault: false, // Allow default Escape behavior
      });
    }

    // Ctrl+S: Save
    if (onSave) {
      result.push({
        key: "s",
        ctrl: true,
        action: onSave,
        description: "Save",
        enabled: canSave,
        preventDefault: true, // Prevent browser save dialog
      });
    }

    // Delete or Ctrl+D: Delete
    if (onDelete) {
      result.push({
        key: "Delete",
        action: onDelete,
        description: "Delete selected",
        enabled: canDelete,
        preventDefault: true,
      });
    }

    // Ctrl/Cmd+F: Focus the search input (overrides the browser Find bar)
    if (onFocusSearch) {
      result.push({
        key: "f",
        ctrl: true,
        action: onFocusSearch,
        description: "Focus search",
        enabled: canFocusSearch,
        preventDefault: true,
      });
    }

    // F9: Open the header overflow (⋯) menu — a function key that browsers
    // leave free.
    if (onOpenMenu) {
      result.push({
        key: "F9",
        action: onOpenMenu,
        description: "Open actions menu",
        enabled: canOpenMenu,
        preventDefault: true,
      });
    }

    // F8: Reset applied filters
    if (onResetFilters) {
      result.push({
        key: "F8",
        action: onResetFilters,
        description: "Reset filters",
        enabled: canResetFilters,
        preventDefault: true,
      });
    }

    // Page-specific extras (tab switching, etc.)
    if (extraShortcuts && extraShortcuts.length > 0) {
      result.push(...extraShortcuts);
    }

    return result;
  }, [
    onAdd,
    onRefresh,
    onEscape,
    onSave,
    onDelete,
    onFocusSearch,
    onOpenMenu,
    onResetFilters,
    canAdd,
    canRefresh,
    canSave,
    canDelete,
    canFocusSearch,
    canOpenMenu,
    canResetFilters,
    extraShortcuts,
  ]);

  useKeyboardShortcuts({
    shortcuts,
    enabled,
    scope: "entity-page",
    onlyWhenNothingOpen: true,
  });
};

// ============================================
// Modal/Drawer Shortcuts Preset
// ============================================

export interface ModalShortcutsOptions {
  /**
   * Esc — ONLY for a surface that does not close on Esc by itself (an inline panel such as the map's zone editor).
   *
   * ⚠⚠ There is deliberately no `onClose` here (ROADMAP #121). `AppDrawer` and `ConfirmModal` already close
   *   on Esc — and theirs refuses while `closable` is false or a save is running, and asks "Unsaved changes — discard?"
   *   when `isDirty`. A second Esc registered here closed the drawer straight past all three: 71 drawers passed their
   *   `onClose`, so typed work was lost with no question and a refusal landed on a closed drawer.
   *   `check-drawer-esc` fails a file that passes `onEscape` and also renders a drawer or modal.
   */
  onEscape?: () => void;

  /** Handler for confirming/saving (Ctrl+Enter or Ctrl+S) */
  onConfirm?: () => void;

  /** Whether confirm action is allowed */
  canConfirm?: boolean;

  /** Whether shortcuts are enabled — pass the drawer's `opened`: an enabled registration joins the stack below. */
  enabled?: boolean;
}

/**
 * Only the TOP open drawer acts on Esc / ⌘S / ⌘Enter (suggested task, 2026-10-03).
 *
 * ⚠⚠ Every registration listens on `document`, so before this ONE Esc closed every open drawer and ONE ⌘S saved every
 *   one of them: a "Create a category" drawer opened from a picker inside an edit drawer took the edit drawer with it
 *   (TaxSelect worked around it with `onCreatingChange`; CategorySelect has 37 callers). A drawer's shortcuts now sit on
 *   @em/ui's overlay stack (with AppDrawer and ConfirmModal) and act only when nothing opened after them —
 *   judged on the stack AS IT WAS WHEN THE KEY WENT DOWN (see overlayStack.ts for why a live read was wrong). The
 *   listeners still all run — the others simply do nothing.
 */
/**
 * Preset hook for modal/drawer shortcuts.
 * Provides Ctrl+S / Ctrl+Enter (save/confirm) — acted on only by the TOP open drawer (see the stack above). Esc is the
 * drawer's own (`AppDrawer`); `onEscape` exists only for a surface that has none.
 */
export const useModalShortcuts = (options: ModalShortcutsOptions): void => {
  const { onEscape, onConfirm, canConfirm = true, enabled = true } = options;
  const tokenRef = useRef<symbol>(Symbol("modal-shortcuts"));

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return pushShortcuts(tokenRef.current);
  }, [enabled]);

  const shortcuts = useMemo<KeyboardShortcut[]>(() => {
    const result: KeyboardShortcut[] = [];
    // Only the top registration acts; the others' listeners run and do nothing.
    const ifTop = (action: () => void) => () => {
      if (shortcutsMayAct(tokenRef.current)) {
        action();
      }
    };

    // Escape: only for a surface with no Esc of its own (see `onEscape`) — a drawer's / modal's Esc is the surface's.
    if (onEscape) {
      result.push({
        key: "Escape",
        action: ifTop(onEscape),
        description: "Close",
        enabled: true,
        preventDefault: false,
      });
    }

    // ⚠ ⌘S / ⌘Enter stay REGISTERED (and swallowed) while the drawer cannot confirm — loading its record, or saving —
    // and simply do nothing. Disabled, the key fell through to the BROWSER: ⌘S opened "Save Page As…" over the drawer
    // (ROADMAP #115 review; memory "a disabled shortcut hands the key back").
    const confirmIfAllowed = () => {
      if (canConfirm && onConfirm) {
        onConfirm();
      }
    };

    // Ctrl+S: Save/Confirm
    if (onConfirm) {
      result.push({
        key: "s",
        ctrl: true,
        action: ifTop(confirmIfAllowed),
        description: "Save/Confirm",
        enabled: true,
        preventDefault: true,
      });
    }

    // Ctrl+Enter: Alternative confirm
    if (onConfirm) {
      result.push({
        key: "Enter",
        ctrl: true,
        action: ifTop(confirmIfAllowed),
        description: "Confirm",
        enabled: true,
        preventDefault: true,
      });
    }

    return result;
  }, [onEscape, onConfirm, canConfirm]);

  useKeyboardShortcuts({
    shortcuts,
    enabled,
    scope: "modal",
  });
};

// ============================================
// Helper: Get Shortcut Display String
// ============================================

/**
 * Get a human-readable string for a shortcut (e.g., "Ctrl+N" or "⌘N")
 */
export const getShortcutDisplayString = (
  shortcut: Pick<KeyboardShortcut, "key" | "ctrl" | "shift" | "alt">,
  useMacSymbols: boolean = typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().includes("MAC")
): string => {
  const parts: string[] = [];

  if (shortcut.ctrl) {
    parts.push(useMacSymbols ? "⌘" : "Ctrl");
  }
  if (shortcut.alt) {
    parts.push(useMacSymbols ? "⌥" : "Alt");
  }
  if (shortcut.shift) {
    parts.push(useMacSymbols ? "⇧" : "Shift");
  }

  // Format key name
  let keyDisplay = shortcut.key;
  if (keyDisplay === "Escape") keyDisplay = useMacSymbols ? "⎋" : "Esc";
  else if (keyDisplay === "Enter") keyDisplay = useMacSymbols ? "↵" : "Enter";
  else if (keyDisplay === "Delete") keyDisplay = useMacSymbols ? "⌫" : "Del";
  else keyDisplay = keyDisplay.toUpperCase();

  parts.push(keyDisplay);

  return useMacSymbols ? parts.join("") : parts.join("+");
};

// ============================================
// Standard entity shortcut hints (for button tooltips)
// ============================================

/**
 * Display hints for the standard entity shortcuts, matching the bindings in
 * useEntityPageShortcuts / useModalShortcuts (⌘I / ⌘R / ⌘S, or Ctrl+… on
 * non-Mac). Append these to Add / Refresh / Save button tooltips so the
 * shortcuts are discoverable. Defined after getShortcutDisplayString so it's
 * initialized when referenced.
 */
export const SHORTCUT_HINTS = {
  add: getShortcutDisplayString({ key: "i", ctrl: true }),
  refresh: getShortcutDisplayString({ key: "r", ctrl: true }),
  save: getShortcutDisplayString({ key: "s", ctrl: true }),
  searchFocus: getShortcutDisplayString({ key: "f", ctrl: true }),
  menu: getShortcutDisplayString({ key: "F9" }),
  resetFilters: getShortcutDisplayString({ key: "F8" }),
  commandPalette: getShortcutDisplayString({ key: "k", ctrl: true }),
};

// ============================================
// Standard shortcut hints for the ShortcutHintBar
// ============================================

/** One hint shown in a ShortcutHintBar: a key combo + what it does. */
export interface ShortcutHint {
  keys: string;
  label: string;
}

export interface EntityPageShortcutHintsOptions {
  /**
   * Include the Command palette (⌘K) hint. Defaults to true — the palette is
   * global, so it's advertised wherever the hint bar appears.
   */
  hasCommandPalette?: boolean;
  /** Include the Add (⌘I) hint. */
  hasAdd?: boolean;
  /** Include the Save (⌘S) hint — for pages that create/edit records. */
  hasSave?: boolean;
  /** Include the Refresh (⌘R) hint. */
  hasRefresh?: boolean;
  /** Include the Focus-search (⌘F) hint. */
  hasSearch?: boolean;
  /** Include the Open-menu (F9) hint. */
  hasMenu?: boolean;
  /** Include the Reset-filters (F8) hint. */
  hasResetFilters?: boolean;
  /** Page-specific extra hints (e.g. tab switching) appended at the end. */
  extra?: ShortcutHint[];
}

/**
 * Builds the standard hint list for the ShortcutHintBar from a page's enabled
 * shortcuts, using shared `common:shortcuts.*` labels — so the display strings
 * stay in sync with the actual bindings (SHORTCUT_HINTS) in one place. Memoize
 * `extra` in the caller to keep the result stable.
 */
export const useEntityPageShortcutHints = (
  options: EntityPageShortcutHintsOptions
): ShortcutHint[] => {
  const { t } = useTranslation(["common"]);
  const {
    hasCommandPalette = true,
    hasAdd,
    hasSave,
    hasRefresh,
    hasSearch,
    hasMenu,
    hasResetFilters,
    extra,
  } = options;

  return useMemo(() => {
    const hints: ShortcutHint[] = [];

    if (hasCommandPalette) {
      hints.push({
        keys: SHORTCUT_HINTS.commandPalette,
        label: t("common:shortcuts.commandPalette", { defaultValue: "Command palette" }),
      });
    }

    if (hasSearch) {
      hints.push({
        keys: SHORTCUT_HINTS.searchFocus,
        label: t("common:shortcuts.search", { defaultValue: "Search" }),
      });
    }

    if (hasAdd) {
      hints.push({
        keys: SHORTCUT_HINTS.add,
        label: t("common:shortcuts.add", { defaultValue: "Add" }),
      });
    }

    if (hasSave) {
      hints.push({
        keys: SHORTCUT_HINTS.save,
        label: t("common:shortcuts.save", { defaultValue: "Save" }),
      });
    }

    if (hasRefresh) {
      hints.push({
        keys: SHORTCUT_HINTS.refresh,
        label: t("common:shortcuts.refresh", { defaultValue: "Refresh" }),
      });
    }

    if (hasResetFilters) {
      hints.push({
        keys: SHORTCUT_HINTS.resetFilters,
        label: t("common:shortcuts.resetFilters", { defaultValue: "Reset filters" }),
      });
    }

    if (hasMenu) {
      hints.push({
        keys: SHORTCUT_HINTS.menu,
        label: t("common:shortcuts.menu", { defaultValue: "Actions" }),
      });
    }

    if (extra && extra.length > 0) {
      hints.push(...extra);
    }

    return hints;
  }, [
    t,
    hasCommandPalette,
    hasAdd,
    hasSave,
    hasRefresh,
    hasSearch,
    hasMenu,
    hasResetFilters,
    extra,
  ]);
};
